/**
 * Product Type Catalog Manager — premium IT Operations modal.
 * Add, edit, deactivate/activate and reorder product types, and configure
 * each type's dynamic field schema. Layout-only redesign: data, ordering
 * logic, permissions and APIs are unchanged.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { X, Plus, Pencil, ArrowUp, ArrowDown, Ban, RotateCcw, Trash2, Boxes, Search, PackageOpen } from 'lucide-react';
import { toast } from 'sonner';
import ITPagination from '../it/ITPagination';
import './ProductTypesModal.css';

const FIELD_TYPES = ['text', 'number', 'select', 'date', 'boolean'];

const emptyField = () => ({ key: '', label: '', type: 'text', required: false, options: [] });

const TypeForm = ({ initial, onSave, onCancel, saving }) => {
  const [name, setName] = useState(initial?.name || '');
  const [fields, setFields] = useState(initial?.field_schema?.map(f => ({ ...f })) || []);

  const updateField = (i, patch) => setFields(fs => fs.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  const removeField = (i) => setFields(fs => fs.filter((_, idx) => idx !== i));

  const submit = (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Type name is required'); return; }
    for (const f of fields) {
      if (!f.label.trim()) { toast.error('Every custom field needs a label'); return; }
      if (f.type === 'select' && !(f.options || []).length) {
        toast.error(`Select field "${f.label}" needs options`); return;
      }
    }
    onSave({ name: name.trim(), field_schema: fields });
  };

  return (
    <form onSubmit={submit} className="ptm-form" data-testid="product-type-form">
      <div className="ptm-form-body">
        <div className="ptm-section">
          <div className="ptm-section-title">Type Details</div>
          <div className="ptm-field">
            <label>Type Name <span className="ptm-req">*</span></label>
            <input
              type="text" value={name} onChange={e => setName(e.target.value)}
              placeholder="e.g., Lenovo Docking Station" required
              data-testid="product-type-name-input"
            />
          </div>
        </div>
        <div className="ptm-section">
          <div className="ptm-section-head">
            <div className="ptm-section-title">Custom Fields <span className="ptm-section-hint">rendered on the Add/Edit Asset form</span></div>
            <button type="button" className="ptm-btn-ghost" data-testid="add-schema-field-btn"
              onClick={() => setFields(fs => [...fs, emptyField()])}>
              <Plus size={13} /> Add Field
            </button>
          </div>
          {fields.length === 0 && (
            <div className="ptm-empty-hint">No custom fields — this type uses only the standard asset fields.</div>
          )}
          {fields.map((f, i) => (
            <div key={i} className="ptm-schema-row">
              <input type="text" value={f.label} placeholder="Label (e.g., Processor)"
                onChange={e => updateField(i, { label: e.target.value })} style={{ flex: 2 }}
                data-testid={`schema-field-label-${i}`} />
              <select value={f.type} onChange={e => updateField(i, { type: e.target.value })}
                style={{ flex: 1 }} data-testid={`schema-field-type-${i}`}>
                {FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              {f.type === 'select' && (
                <input type="text" value={(f.options || []).join(', ')} placeholder="Options, comma-separated"
                  onChange={e => updateField(i, { options: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                  style={{ flex: 2 }} data-testid={`schema-field-options-${i}`} />
              )}
              <label className="ptm-req-check">
                <input type="checkbox" checked={!!f.required}
                  onChange={e => updateField(i, { required: e.target.checked })}
                  data-testid={`schema-field-required-${i}`} /> Req
              </label>
              <button type="button" className="ptm-icon-btn ptm-danger" onClick={() => removeField(i)}
                title="Remove field" data-testid={`schema-field-remove-${i}`}>
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      </div>
      <div className="ptm-footer">
        <button type="button" className="ptm-btn-cancel" onClick={onCancel}>Cancel</button>
        <button type="submit" className="ptm-btn-save" disabled={saving} data-testid="save-product-type-btn">
          {saving ? 'Saving…' : (initial ? 'Save Changes' : 'Create Type')}
        </button>
      </div>
    </form>
  );
};

export const ProductTypesModal = ({ isOpen, onClose, apiCall, onChanged }) => {
  const [types, setTypes] = useState([]);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const fetchTypes = useCallback(async () => {
    try {
      const data = await apiCall('/api/it-asset-types?include_inactive=true');
      setTypes(data.types || []);
    } catch (e) {
      toast.error('Failed to load product types');
    }
  }, [apiCall]);

  useEffect(() => {
    if (isOpen) {
      fetchTypes();
      setEditing(null); setAdding(false);
      setSearch(''); setStatusFilter('all'); setPage(1);
    }
  }, [isOpen, fetchTypes]);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  useEffect(() => { setPage(1); }, [search, statusFilter, limit]);

  const notify = () => { fetchTypes(); onChanged && onChanged(); };

  const save = async (payload) => {
    setSaving(true);
    try {
      if (editing) {
        await apiCall(`/api/it-asset-types/${editing.id}`, { method: 'PUT', body: JSON.stringify(payload) });
        toast.success('Product type updated');
      } else {
        await apiCall('/api/it-asset-types', { method: 'POST', body: JSON.stringify(payload) });
        toast.success('Product type created');
      }
      setEditing(null); setAdding(false);
      notify();
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const toggleActive = async (t) => {
    try {
      await apiCall(`/api/it-asset-types/${t.id}/${t.is_active ? 'deactivate' : 'activate'}`, { method: 'POST' });
      toast.success(t.is_active
        ? `"${t.name}" deactivated — existing assets remain visible`
        : `"${t.name}" reactivated`);
      notify();
    } catch (e) { toast.error(e.message); }
  };

  const move = async (fullIdx, dir) => {
    const next = [...types];
    const j = fullIdx + dir;
    if (fullIdx < 0 || j < 0 || j >= next.length) return;
    [next[fullIdx], next[j]] = [next[j], next[fullIdx]];
    setTypes(next);
    try {
      await apiCall('/api/it-asset-types/reorder', {
        method: 'POST', body: JSON.stringify({ ordered_ids: next.map(t => t.id) })
      });
      onChanged && onChanged();
    } catch (e) { toast.error('Reorder failed'); fetchTypes(); }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return types.filter(t => {
      if (statusFilter === 'active' && !t.is_active) return false;
      if (statusFilter === 'inactive' && t.is_active) return false;
      if (q && !t.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [types, search, statusFilter]);

  const filtersActive = search.trim() !== '' || statusFilter !== 'all';
  const paged = useMemo(() => filtered.slice((page - 1) * limit, page * limit), [filtered, page, limit]);

  if (!isOpen) return null;

  return (
    <div className="avm-overlay ptm-overlay" onClick={onClose}>
      <div className="ptm-modal" onClick={e => e.stopPropagation()} data-testid="product-types-modal">
        <div className="ptm-header">
          <div className="ptm-header-left">
            <div className="ptm-header-icon"><Boxes size={19} /></div>
            <div>
              <h3>Product Type Catalog</h3>
              <p>Manage IT asset product types and field configurations</p>
            </div>
          </div>
          <button className="ptm-close" onClick={onClose} data-testid="product-types-close" aria-label="Close">
            <X size={16} />
          </button>
        </div>

        {(adding || editing) ? (
          <TypeForm initial={editing} saving={saving} onSave={save}
            onCancel={() => { setAdding(false); setEditing(null); }} />
        ) : (
          <>
            <div className="ptm-toolbar">
              <div className="ptm-search">
                <Search size={14} />
                <input
                  type="text" value={search} placeholder="Search product types..."
                  onChange={e => setSearch(e.target.value)}
                  data-testid="ptm-search-input"
                />
              </div>
              <select className="ptm-status-filter" value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)} data-testid="ptm-status-filter">
                <option value="all">Status: All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <span className="ptm-count">{filtered.length} of {types.length} types</span>
              <button className="ptm-btn-save ptm-add-btn" onClick={() => setAdding(true)} data-testid="add-product-type-btn">
                <Plus size={14} /> Add Type
              </button>
            </div>

            <div className="ptm-table-wrap">
              {paged.length === 0 ? (
                <div className="ptm-empty">
                  <PackageOpen size={30} />
                  <span>{filtersActive ? 'No product types match your search.' : 'No product types yet — add your first type.'}</span>
                </div>
              ) : (
                <table className="ptm-table">
                  <thead>
                    <tr>
                      <th className="ptm-col-order">Order</th>
                      <th className="ptm-col-name">Product Type</th>
                      <th className="ptm-col-fields">Custom Fields</th>
                      <th className="ptm-col-use">In Use</th>
                      <th className="ptm-col-status">Status</th>
                      <th className="ptm-col-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paged.map((t) => {
                      const fullIdx = types.findIndex(x => x.id === t.id);
                      const fieldCount = (t.field_schema || []).length;
                      return (
                        <tr key={t.id} className={t.is_active ? '' : 'ptm-row-inactive'} data-testid={`type-row-${t.slug}`}>
                          <td className="ptm-col-order">
                            <div className="ptm-order-ctrls" title={filtersActive ? 'Clear search/filter to reorder' : undefined}>
                              <button className="ptm-icon-btn" onClick={() => move(fullIdx, -1)}
                                disabled={filtersActive || fullIdx <= 0} title="Move up"
                                data-testid={`type-move-up-${t.slug}`}><ArrowUp size={13} /></button>
                              <button className="ptm-icon-btn" onClick={() => move(fullIdx, 1)}
                                disabled={filtersActive || fullIdx === types.length - 1} title="Move down"
                                data-testid={`type-move-down-${t.slug}`}><ArrowDown size={13} /></button>
                            </div>
                          </td>
                          <td className="ptm-col-name">
                            <div className="ptm-name">{t.name}</div>
                            <div className="ptm-name-sub">
                              {fieldCount ? `${fieldCount} custom field${fieldCount === 1 ? '' : 's'}` : 'Standard fields only'}
                            </div>
                          </td>
                          <td className="ptm-col-fields">
                            {fieldCount
                              ? <span className="ptm-fields-list">{t.field_schema.map(f => f.label).join(', ')}</span>
                              : <span className="ptm-muted">Standard fields only</span>}
                          </td>
                          <td className="ptm-col-use" data-testid={`type-count-${t.slug}`}>
                            {t.asset_count > 0
                              ? <span className="ptm-use-badge">{t.asset_count} in use</span>
                              : <span className="ptm-muted">—</span>}
                          </td>
                          <td className="ptm-col-status">
                            <span className={`ptm-status ${t.is_active ? 'ptm-status-active' : 'ptm-status-inactive'}`}>
                              {t.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="ptm-col-actions">
                            <div className="ptm-actions">
                              <button className="ptm-icon-btn" onClick={() => setEditing(t)} title="Edit"
                                data-testid={`type-edit-${t.slug}`}><Pencil size={13} /></button>
                              <button className="ptm-icon-btn" onClick={() => toggleActive(t)}
                                title={t.is_active ? 'Deactivate' : 'Reactivate'}
                                data-testid={`type-toggle-${t.slug}`}>
                                {t.is_active ? <Ban size={13} /> : <RotateCcw size={13} />}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            <div className="ptm-pagination-bar">
              <ITPagination
                page={page} limit={limit} total={filtered.length}
                onPageChange={setPage} onLimitChange={setLimit}
                testid="ptm-pagination"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProductTypesModal;
